import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useLocation } from "react-router-dom";

import useAppState from "../../context/useAppState";
import CustomSelect from "./CustomSelect";

// Icons
import homeIcon from "../../assets/images/home-icon.svg";
import feteIcon from "../../assets/images/fete-icon.svg";
import exploreIcon from "../../assets/images/explore-icon.svg";
import favIcon from "../../assets/images/favrite-icon.svg";
import settingsIcon from "../../assets/images/settings-icon.svg";
import myFeetIcon from "../../assets/images/myfeet-icon.svg";
import logoutIcon from '../../assets/images/logout-icon.svg';
import rightSideIcon2 from '../../assets/images/right-side-icon2.svg';
import mobileLogo from '../../assets/images/feteboard-mobile-icon.svg';
import profileIcon from '../../assets/images/profile-icon.png';

const Sidebar = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const location = useLocation();
    const { pathname } = location;

    const { handleClick, showSidebar } = useAppState('sidebar');
    const { retrieveUserTeams, hasRetrievedTeams, handleChangeTeam, selectedTeam, teamList, setTeamChange } = useAppState('selectTeam');

    useEffect(() => {
        // hasRetrievedTeams than no need to call api
        if (!hasRetrievedTeams) {
            retrieveUserTeams();
        }
    }, [hasRetrievedTeams, selectedTeam]);

    // To overcome issue of re-rendering sidebar when new team is created via add new team button
    useEffect(() => {
        setTimeout(() => {
            if (localStorage.getItem('add_new_team')) {
                retrieveUserTeams();
                localStorage.removeItem('add_new_team')
            }
        }, 0)
    });

    const { setState: setEventListData, updateUserEventCallApiFlag } = useAppState("eventList");

    const handleChange = (value: any) => {
        if (value?.value == 'pendingInvitation') return;

        handleChangeTeam(value);
        if(value?.value != 'addNewTeam') {
            setTeamChange(true);
        }
        updateUserEventCallApiFlag(true);
    }

    return (
        <div className={`w-60 border-r border-[#D0D5DD] ${!showSidebar ? 'collapsed-sidebar' : ''}  smoothness switch-to-overlay`}>
            {/* Logo */}
            <div className="border-b border-[#D0D5DD] py-[11px] px-[18px] relative collapsed-logo-wrapper smoothness cursor-pointer">
                <div onClick={() => navigate('/')} className="w-[208px] gap-x-2 flex items-center collapsed-logo smoothness">
                    <img className='min-w-[40px] min-h-[32px] object-contain shrink-0 smoothness' src={mobileLogo} alt="main logo" />
                    <span className="font-medium text-[#101828] text-[27px] smoothness">FeteBoard</span>
                </div>
                <span className="absolute border border-[#D0D5DD] rounded-full w-6 h-6 flex items-center justify-center cursor-pointer -bottom-3 -right-3 z-10 bg-white collapsed-icon smoothness hover:border-[#1e0af5]">
                    <img className="transform rotate-180 smoothness" src={rightSideIcon2} alt="" onClick={handleClick} />
                </span>
            </div>
            <div className="pt-4 px-3 pb-4">
                <ul className='sidebar-selectbox sidebar-dropdown-adjust disable-hover-on-focus'>
                    <CustomSelect
                        selectedValue={selectedTeam}
                        options={teamList}
                        onChange={(value: any) => handleChange(value)}
                        extendedOption={true}
                        className="on-hover-border-blue rounded"
                    />

                    <li className={`${pathname === '/' && 'active'} group relative flex items-center gap-x-3 py-2 hover:bg-[#E8E6FE] rounded-[5px] cursor-pointer mb-2 collapsed-list justify-start sidebar-menu`} onClick={() => navigate('/')} >
                        <img className='inline-block ml-2.5 w-6 h-6 on-hover-blue' src={homeIcon} alt="home" />
                        <Link className='font-interMedium group-hover:text-[#1E0AF5] text-base text-[#344054] inline-block smoothness' to="/">Home</Link>
                        <span className='group-hover:inline-block hidden absolute -left-3 w-[5px] h-10 bg-[#1E0AF5] rounded-t-none rounded-r-[3px] rounded-b-[3px] rounded-l-none sidebar-menu-blue-line'></span>
                    </li>
                    
                    <li className={`${pathname.includes('/fete') && 'active'} group relative flex items-center gap-x-3 py-2 hover:bg-[#E8E6FE] rounded-[5px] cursor-pointer mb-2 collapsed-list justify-start sidebar-menu`} >
                        <img className='inline-block ml-2.5 w-6 h-6 on-hover-blue' src={feteIcon} alt="Fete" />
                        <Link className='font-interMedium group-hover:text-[#1E0AF5] text-base text-[#344054] inline-block smoothness' to="/">Fete</Link>
                        <span className='group-hover:inline-block hidden absolute -left-3 w-[5px] h-10 bg-[#1E0AF5] rounded-t-none rounded-r-[3px] rounded-b-[3px] rounded-l-none sidebar-menu-blue-line'></span>
                    </li>

                    <li className='group relative flex items-center gap-x-3 py-2 hover:bg-[#E8E6FE] rounded-[5px] cursor-pointer mb-2 collapsed-list justify-start sidebar-menu'>
                        <img className='inline-block ml-2.5 w-6 h-6 on-hover-blue' src={exploreIcon} alt="Explore" />
                        <a className='font-interMedium group-hover:text-[#1E0AF5] text-base text-[#344054] inline-block smoothness' href="#">Explore</a>
                        <span className='group-hover:inline-block hidden absolute -left-3 w-[5px] h-10 bg-[#1E0AF5] rounded-t-none rounded-r-[3px] rounded-b-[3px] rounded-l-none sidebar-menu-blue-line'></span>
                    </li>

                    <li className={`${pathname === '/favourite' && 'active'} group relative flex items-center gap-x-3 py-2 hover:bg-[#e8e6fe] rounded-[5px] cursor-pointer mb-2 collapsed-list justify-start sidebar-menu`} onClick={() => navigate('/favourite')}>
                        <img className='inline-block ml-2.5 w-6 h-6 on-hover-blue' src={favIcon} alt="Favourites" />
                        <a className='font-interMedium group-hover:text-[#1E0AF5] text-base text-[#344054] inline-block smoothness' href="#">Favourites</a>
                        <span className='group-hover:inline-block hidden absolute -left-3 w-[5px] h-10 bg-[#1E0AF5] rounded-t-none rounded-r-[3px] rounded-b-[3px] rounded-l-none sidebar-menu-blue-line'></span>
                    </li>

                    <li className={`${pathname === '/settings' && 'active'} group relative flex items-center gap-x-3 py-2 hover:bg-[#E8E6FE] rounded-[5px] cursor-pointer mb-2 collapsed-list justify-start sidebar-menu`} onClick={() => navigate('/settings')} >
                        <img className='inline-block ml-2.5 w-6 h-6 on-hover-blue' src={settingsIcon} alt="Settings" />
                        <Link className='font-interMedium group-hover:text-[#1E0AF5] text-base text-[#344054] inline-block smoothness' to="/settings">Settings</Link>
                        <span className='group-hover:inline-block hidden absolute -left-3 w-[5px] h-10 bg-[#1E0AF5] rounded-t-none rounded-r-[3px] rounded-b-[3px] rounded-l-none sidebar-menu-blue-line'></span>
                    </li>

                </ul>

                {/* <div className="h-px w-full bg-[#EAECF0] mt-4 mb-5 collapsed-dash smoothness"></div> */}

                {/* My Fetes */}
                {/* <div className="collapsed-fate whitespace-nowrap smoothness">
                    <div className="flex items-center justify-between pl-3 pb-5">
                        <span className='text-xs text-[#6B778C] font-interSemiBold uppercase whitespace-nowrap'>My Fetes</span>
                        <img className='pr-2 cursor-pointer' src={myFeetIcon} alt="feet" />
                    </div>

                    <ul className='pl-5 whitespace-nowrap'>
                        <li className='flex items-center gap-x-2.5 mb-[18px]'>
                            <span className='w-2 h-2 rounded-full bg-[#B692F6] inline-block'></span>
                            <a className='text-[#42526E] font-interSemiBold text-sm whitespace-nowrap' href="">Company Picnic</a>
                        </li>
                        <li className='flex items-center gap-x-2.5'>
                            <span className='bg-[#36BFFA] w-2 h-2 rounded-full inline-block'></span>
                            <a className='text-[#42526E] font-interSemiBold text-sm whitespace-nowrap' href="">Annual Retreat</a>
                        </li>
                    </ul>
                </div> */}
            </div>
        </div>
    )
}

export default Sidebar;